import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { SessionResponse } from '@portal/contracts';
import { ApiError, get, onAuthEvent, setCsrfToken } from './api';

export const SESSION_KEY = ['session'] as const;

async function fetchSession(): Promise<SessionResponse | null> {
  try {
    const s = await get<SessionResponse>('/auth/session', undefined, { business: false });
    setCsrfToken(s.csrfToken); // before any dependent request is issued
    return s;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw e;
  }
}

interface SessionValue {
  session: SessionResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}
const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: SESSION_KEY, queryFn: fetchSession, staleTime: 5 * 60_000, retry: false });

  // Any 401 from anywhere drops us back to the sign-in screen.
  useEffect(
    () =>
      onAuthEvent((e) => {
        if (e.type === 'unauthenticated') {
          setCsrfToken(null);
          qc.setQueryData(SESSION_KEY, null);
          qc.removeQueries({ predicate: (query) => query.queryKey[0] === 'b' });
        } else {
          void qc.invalidateQueries({ queryKey: SESSION_KEY });
          navigate('/mfa');
        }
      }),
    [qc, navigate],
  );

  const value = useMemo<SessionValue>(
    () => ({ session: q.data ?? null, isLoading: q.isLoading, error: q.error, refetch: q.refetch }),
    [q.data, q.isLoading, q.error, q.refetch],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used inside <SessionProvider>');
  return v;
}
